<template>
  <div>
    <v-menu
      ref="menu"
      v-model="menu"
      :close-on-content-click="false"
      transition="scale-transition"
      offset-y
      max-width="290px"
      min-width="290px"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-text-field
          v-model="dateFormatted"
          :label="label"
          readonly
          v-bind="attrs"
          v-on="on"
          :rules="rules"
          :disabled="disabled"
          :clearable="clearable"
          :hint="hint"
          :persistent-hint="persistentHint"
          :placeholder="placeholder"
          :prepend-icon="prependIcon"
          :append-icon="appendIcon"
          :outlined="outlined"
          :filled="filled"
          :dense="dense"
          :rounded="rounded"
          :shaped="shaped"
          :solo="solo"
          :flat="flat"
          :elevation="elevation"
          :color="color"
          :background-color="backgroundColor"
          :class="customClass"
        ></v-text-field>
      </template>
      <v-date-picker
        v-model="date"
        :max="maxDateFormatted"
        :min="minDateFormatted"
        :allowed-dates="allowedDates"
        :first-day-of-week="firstDayOfWeek"
        :locale="locale"
        :scrollable="scrollable"
        :no-title="noTitle"
        :landscape="landscape"
        :reactive="reactive"
        :readonly="readonly"
        :disabled="disabled"
        :color="color"
        :header-color="headerColor"
        :year-icon="yearIcon"
        :prev-icon="prevIcon"
        :next-icon="nextIcon"
        :elevation="elevation"
        :width="width"
        :height="height"
        :class="pickerClass"
        @input="onDateChange"
      >
      </v-date-picker>
    </v-menu>
  </div>
</template>

<script>
export default {
  name: 'DatePicker',
  props: {
    value: {
      type: [String, Date],
      default: null
    },
    label: {
      type: String,
      default: 'Date'
    },
    rules: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: false
    },
    hint: {
      type: String,
      default: ''
    },
    persistentHint: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ''
    },
    prependIcon: {
      type: String,
      default: 'mdi-calendar'
    },
    appendIcon: {
      type: String,
      default: ''
    },
    outlined: {
      type: Boolean,
      default: false
    },
    filled: {
      type: Boolean,
      default: false
    },
    dense: {
      type: Boolean,
      default: false
    },
    rounded: {
      type: Boolean,
      default: false
    },
    shaped: {
      type: Boolean,
      default: false
    },
    solo: {
      type: Boolean,
      default: false
    },
    flat: {
      type: Boolean,
      default: false
    },
    elevation: {
      type: [Number, String],
      default: undefined
    },
    color: {
      type: String,
      default: 'primary'
    },
    backgroundColor: {
      type: String,
      default: ''
    },
    customClass: {
      type: String,
      default: ''
    },
    max: {
      type: [String, Date],
      default: null
    },
    min: {
      type: [String, Date],
      default: null
    },
    allowedDates: {
      type: Function,
      default: null
    },
    firstDayOfWeek: {
      type: [String, Number],
      default: 0
    },
    locale: {
      type: String,
      default: 'th'
    },
    scrollable: {
      type: Boolean,
      default: false
    },
    noTitle: {
      type: Boolean,
      default: false
    },
    landscape: {
      type: Boolean,
      default: false
    },
    reactive: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    headerColor: {
      type: String,
      default: ''
    },
    yearIcon: {
      type: String,
      default: 'mdi-calendar-blank'
    },
    prevIcon: {
      type: String,
      default: 'mdi-chevron-left'
    },
    nextIcon: {
      type: String,
      default: 'mdi-chevron-right'
    },
    width: {
      type: [Number, String],
      default: undefined
    },
    height: {
      type: [Number, String],
      default: undefined
    },
    pickerClass: {
      type: String,
      default: ''
    },

    format: {
      type: String,
      default: 'YYYY-MM-DD'
    }
  },
  data() {
    return {
      menu: false,
      date: null
    }
  },
  computed: {
    dateFormatted() {
      if (!this.date) return ''
      
      try {
        // Format date based on the format prop
        const dateObj = new Date(this.date)
        if (isNaN(dateObj.getTime())) return ''
        
        if (this.format === 'DD/MM/YYYY') {
          return dateObj.toLocaleDateString('th-TH')
        } else if (this.format === 'MM/DD/YYYY') {
          return dateObj.toLocaleDateString('en-US')
        } else {
          // Default YYYY-MM-DD format
          return this.date
        }
      } catch (error) {
        console.error('Date formatting error:', error)
        return this.date || ''
      }
    },
    maxDateFormatted() {
      if (!this.max) return null
      if (this.max instanceof Date) {
        const year = this.max.getFullYear()
        const month = String(this.max.getMonth() + 1).padStart(2, '0')
        const day = String(this.max.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
      }
      return this.max
    },
    minDateFormatted() {
      if (!this.min) return null
      if (this.min instanceof Date) {
        const year = this.min.getFullYear()
        const month = String(this.min.getMonth() + 1).padStart(2, '0')
        const day = String(this.min.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
      }
      return this.min
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.date = this.formatDateForPicker(newVal)
        } else {
          this.date = null
        }
      }
    }
  },
  methods: {
    formatDateForPicker(date) {
      if (!date) return null
      
      if (typeof date === 'string') {
        return date
      }
      
      if (date instanceof Date) {
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
      }
      
      return null
    },
    openMenu() {
      this.menu = true
    },
    closeMenu() {
      this.menu = false
    },
    onDateChange(value) {
      this.$emit('input', value)
      this.$emit('change', value)
      // Auto close the menu when a date is selected
      this.closeMenu()
    }
  }
}
</script>

<style scoped>
.v-menu__content {
  border-radius: 8px;
}

.v-date-picker-table {
  border-radius: 8px;
}

.v-date-picker-table__events {
  border-radius: 8px;
}
</style> 